import React, { useEffect, useMemo, useState } from "react";
import { Button, FormControl, Table } from "react-bootstrap";
import LoadingSpinner from "views/Components/loadingSpinner";
import {
  getOperationsLoadingDashboard,
  getOperationsPackingBatch,
  markOperationsItemsOrdered,
} from "api/operations/loadingDashboard";
import "./loadingDashboard.css";

const tabs = [
  { key: "orders", label: "By Order" },
  { key: "items", label: "Pick By Item" },
  { key: "routes", label: "Delivery Routes" },
  { key: "procurement", label: "Shortage Procurement" },
];

const statusOptions = [
  { value: "", label: "All active" },
  { value: "1", label: "Submitted" },
  { value: "2", label: "Processing" },
  { value: "10", label: "Contacted" },
  { value: "11", label: "Packed" },
  { value: "12", label: "Ready" },
  { value: "14", label: "Item Pending" },
  { value: "17", label: "Procurement Ready" },
];

const deliveryOptions = [
  { value: "", label: "All" },
  { value: "1", label: "Standard" },
  { value: "2", label: "Express" },
  { value: "3", label: "Islandwide" },
  { value: "5", label: "Pickup" },
];

const stockLabels = {
  packed: "Packed",
  available: "In stock",
  partial: "Partial",
  no_stock: "No stock",
};

const procurementLabels = {
  packed: "Packed",
  ordered: "Ordered",
  not_ordered: "Not ordered",
  not_required: "No order needed",
};

const packStateLabels = {
  packed: "Packed",
  ready_to_pack: "Ready",
  blocked_by_stock: "Blocked",
};

const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch (error) {
    return {};
  }
};

const toDateInputValue = (date) => date.toISOString().slice(0, 10);

const defaultDateFilters = () => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 3);

  return {
    start_date: toDateInputValue(startDate),
    end_date: toDateInputValue(endDate),
  };
};

const formatQuantity = (value) => {
  const number = Number(value || 0);
  return Number.isInteger(number) ? String(number) : number.toFixed(2);
};

const chip = (value, labelMap = {}) => (
  <span className={`operations-chip ${value}`}>
    {labelMap[value] || value || "Unknown"}
  </span>
);

const EmptyState = ({ children }) => (
  <div className="operations-empty">{children || "No pending items found."}</div>
);

const LoadingDashboard = ({ history }) => {
  const user = readUser();
  const token = user.token;
  const [activeTab, setActiveTab] = useState("orders");
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [packingBatch, setPackingBatch] = useState(null);
  const [filters, setFilters] = useState({
    ...defaultDateFilters(),
    order_type: "all",
    status: "",
    district: "",
    city: "",
    delivery_type: "",
    store_id: user.store_id || "",
  });

  const summary = dashboard && dashboard.summary ? dashboard.summary : {};

  const loadDashboard = async () => {
    if (!token) {
      setError("Please login again to view Operations Loading.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await getOperationsLoadingDashboard(token, filters);
      setDashboard(res.data);
      setSelectedOrderIds([]);
    } catch (requestError) {
      setError(
        requestError.response &&
          requestError.response.data &&
          requestError.response.data.message
          ? requestError.response.data.message
          : "Unable to load the operations dashboard.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilter = (key, value) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const openOrder = (orderId) => {
    if (history && history.push) {
      history.push(`/admin/order/${orderId}`);
    }
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds((current) =>
      current.includes(orderId)
        ? current.filter((id) => id !== orderId)
        : current.concat(orderId),
    );
  };

  const toggleAllOrders = (orders) => {
    const orderIds = orders.map((order) => order.order_id);
    const allSelected =
      orderIds.length > 0 && orderIds.every((id) => selectedOrderIds.includes(id));

    setSelectedOrderIds(allSelected ? [] : orderIds);
  };

  const preparePackingBatch = async () => {
    if (!selectedOrderIds.length) {
      setError("Select at least one order before preparing a batch.");
      return;
    }

    setBatchLoading(true);
    setError("");
    try {
      const res = await getOperationsPackingBatch(token, selectedOrderIds);
      setPackingBatch(res.data);
      setActiveTab("batch");
    } catch (requestError) {
      setError(
        requestError.response &&
          requestError.response.data &&
          requestError.response.data.message
          ? requestError.response.data.message
          : "Unable to prepare the packing batch.",
      );
    } finally {
      setBatchLoading(false);
    }
  };

  const markOrdered = async (orderItemIds) => {
    if (!orderItemIds || !orderItemIds.length) {
      return;
    }

    setActionLoading(true);
    setError("");
    try {
      await markOperationsItemsOrdered(token, orderItemIds);
      await loadDashboard();
      setActiveTab("procurement");
    } catch (requestError) {
      setError(
        requestError.response &&
          requestError.response.data &&
          requestError.response.data.message
          ? requestError.response.data.message
          : "Unable to mark selected items as ordered.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const procurementNeedingAction = useMemo(() => {
    if (!dashboard || !dashboard.procurement) {
      return [];
    }

    return dashboard.procurement.filter(
      (item) => item.procurement_state === "not_ordered",
    );
  }, [dashboard]);

  const markAllShortagesOrdered = () => {
    const ids = procurementNeedingAction.reduce(
      (all, item) => all.concat(item.order_item_ids || []),
      [],
    );
    markOrdered(ids);
  };

  return (
    <div className="main-content operations-loading-page">
      <div className="operations-header">
        <div>
          <h3 className="operations-title">Operations Loading</h3>
          <p className="operations-subtitle">
            Packable orders, grouped picking, delivery batches, and shortage
            procurement in one place.
          </p>
        </div>
        <Button bsStyle="info" onClick={loadDashboard} disabled={loading}>
          Refresh
        </Button>
      </div>

      <div className="operations-filters">
        <div className="operations-filter-grid">
          <div>
            <label>Start Date</label>
            <FormControl
              type="date"
              value={filters.start_date}
              onChange={(event) => updateFilter("start_date", event.target.value)}
            />
          </div>
          <div>
            <label>End Date</label>
            <FormControl
              type="date"
              value={filters.end_date}
              onChange={(event) => updateFilter("end_date", event.target.value)}
            />
          </div>
          <div>
            <label>B2B / B2C</label>
            <FormControl
              componentClass="select"
              value={filters.order_type}
              onChange={(event) =>
                updateFilter("order_type", event.target.value)
              }
            >
              <option value="all">All</option>
              <option value="b2b">B2B</option>
              <option value="b2c">B2C</option>
            </FormControl>
          </div>
          <div>
            <label>Status</label>
            <FormControl
              componentClass="select"
              value={filters.status}
              onChange={(event) => updateFilter("status", event.target.value)}
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </FormControl>
          </div>
          <div>
            <label>District</label>
            <FormControl
              value={filters.district}
              placeholder="Colombo"
              onChange={(event) => updateFilter("district", event.target.value)}
            />
          </div>
          <div>
            <label>City</label>
            <FormControl
              value={filters.city}
              placeholder="Nugegoda"
              onChange={(event) => updateFilter("city", event.target.value)}
            />
          </div>
          <div>
            <label>Delivery</label>
            <FormControl
              componentClass="select"
              value={filters.delivery_type}
              onChange={(event) =>
                updateFilter("delivery_type", event.target.value)
              }
            >
              {deliveryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </FormControl>
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <Button bsStyle="primary" onClick={loadDashboard} disabled={loading}>
            Apply Filters
          </Button>
        </div>
      </div>

      {error ? <div className="operations-error">{error}</div> : null}

      <div className="operations-summary-grid">
        <Metric label="Pending Orders" value={summary.total_orders} />
        <Metric label="Ready To Pack" value={summary.ready_to_pack_orders} />
        <Metric label="Blocked Orders" value={summary.blocked_orders} />
        <Metric label="Shortage Items" value={summary.shortage_items} />
      </div>

      <div className="operations-batch-bar">
        <div>
          <strong>{selectedOrderIds.length} order(s) selected</strong>
          <span>
            Select the orders the pharmacist wants to pack together, then prepare
            a grouped picking batch.
          </span>
        </div>
        <Button
          bsStyle="success"
          disabled={batchLoading || !selectedOrderIds.length}
          onClick={preparePackingBatch}
        >
          {batchLoading ? "Preparing Batch" : "Prepare Packing Batch"}
        </Button>
      </div>

      <div className="operations-tabs">
        {tabs.concat(packingBatch ? [{ key: "batch", label: "Packing Batch" }] : []).map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`operations-tab ${activeTab === tab.key ? "active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="operations-panel">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            {activeTab === "orders" && (
              <OrdersTab
                orders={(dashboard && dashboard.orders) || []}
                openOrder={openOrder}
                selectedOrderIds={selectedOrderIds}
                toggleOrderSelection={toggleOrderSelection}
                toggleAllOrders={toggleAllOrders}
              />
            )}
            {activeTab === "items" && (
              <ItemsTab items={(dashboard && dashboard.picking_plan) || []} />
            )}
            {activeTab === "routes" && (
              <RoutesTab routes={(dashboard && dashboard.delivery_groups) || []} />
            )}
            {activeTab === "procurement" && (
              <ProcurementTab
                items={(dashboard && dashboard.procurement) || []}
                actionLoading={actionLoading}
                markOrdered={markOrdered}
                markAllShortagesOrdered={markAllShortagesOrdered}
                hasActionItems={procurementNeedingAction.length > 0}
              />
            )}
            {activeTab === "batch" && (
              <PackingBatchTab batch={packingBatch} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const Metric = ({ label, value }) => (
  <div className="operations-metric">
    <span>{label}</span>
    <strong>{value || 0}</strong>
  </div>
);

const OrdersTab = ({
  orders,
  openOrder,
  selectedOrderIds,
  toggleOrderSelection,
  toggleAllOrders,
}) => {
  const [expandedOrderIds, setExpandedOrderIds] = useState([]);

  if (!orders.length) {
    return <EmptyState />;
  }

  const toggleExpanded = (orderId) => {
    setExpandedOrderIds((current) =>
      current.includes(orderId)
        ? current.filter((id) => id !== orderId)
        : current.concat(orderId),
    );
  };

  const allSelected =
    orders.length > 0 &&
    orders.every((order) => selectedOrderIds.includes(order.order_id));

  return (
    <div className="operations-table-wrap">
      <Table striped hover className="operations-table">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                checked={allSelected}
                onChange={() => toggleAllOrders(orders)}
              />
            </th>
            <th>Order</th>
            <th>Customer</th>
            <th>Route</th>
            <th>Status</th>
            <th>Pending Items</th>
            <th>Pack State</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const isExpanded = expandedOrderIds.includes(order.order_id);
            const visibleItems = isExpanded
              ? order.items || []
              : (order.items || []).slice(0, 4);
            const hiddenCount = Math.max(((order.items || []).length) - 4, 0);

            return (
            <tr key={order.order_id}>
              <td>
                <input
                  type="checkbox"
                  checked={selectedOrderIds.includes(order.order_id)}
                  onChange={() => toggleOrderSelection(order.order_id)}
                />
              </td>
              <td>#{order.order_id}</td>
              <td>
                <strong>{order.customer_name || "Customer"}</strong>
                <div className="operations-muted">{order.order_type}</div>
              </td>
              <td>
                {order.district || "No district"}
                <div className="operations-muted">
                  {order.city_name || "No city"} / {order.delivery_type_label}
                </div>
              </td>
              <td>{order.order_status_label}</td>
              <td>
                <ul className="operations-list">
                  {visibleItems.map((item) => (
                    <li key={item.order_item_id}>
                      {item.medication_title} x {formatQuantity(item.quantity)}{" "}
                      {chip(item.stock_state, stockLabels)}
                    </li>
                  ))}
                </ul>
                {hiddenCount > 0 ? (
                  <button
                    className="operations-more-button"
                    type="button"
                    onClick={() => toggleExpanded(order.order_id)}
                  >
                    {isExpanded ? "Show less" : `+${hiddenCount} more`}
                  </button>
                ) : null}
              </td>
              <td>
                {order.short_items_count > 0
                  ? chip("no_stock", { no_stock: "Blocked" })
                  : chip("available", { available: "Ready" })}
              </td>
              <td>
                <Button bsSize="xsmall" onClick={() => openOrder(order.order_id)}>
                  Open
                </Button>
              </td>
            </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

const PackingBatchTab = ({ batch }) => {
  const [batchView, setBatchView] = useState("items");

  if (!batch) {
    return <EmptyState>Select orders and prepare a packing batch.</EmptyState>;
  }

  const summary = batch.summary || {};
  const items = batch.picking_plan || [];
  const orders = batch.orders || [];

  return (
    <>
      <div className="operations-batch-summary">
        <Metric label="Selected Orders" value={summary.orders_count} />
        <Metric label="All Items" value={summary.items_count} />
        <Metric label="Packed Items" value={summary.packed_items} />
        <Metric label="Pending Items" value={summary.pending_items} />
        <Metric label="Shortage Items" value={summary.shortage_items} />
      </div>
      <div className="operations-subtabs">
        <button
          type="button"
          className={batchView === "items" ? "active" : ""}
          onClick={() => setBatchView("items")}
        >
          Pick By Item
        </button>
        <button
          type="button"
          className={batchView === "orders" ? "active" : ""}
          onClick={() => setBatchView("orders")}
        >
          Verify By Order
        </button>
      </div>
      {batchView === "items" ? (
        <BatchItemsTable items={items} />
      ) : (
        <BatchOrdersTable orders={orders} />
      )}
    </>
  );
};

const BatchItemsTable = ({ items }) => {
  if (!items.length) {
    return <EmptyState>No items found for the selected orders.</EmptyState>;
  }

  return (
    <div className="operations-table-wrap">
      <Table striped hover className="operations-table operations-batch-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Total Needed</th>
            <th>Available</th>
            <th>Short</th>
            <th>Orders</th>
            <th>Order Breakdown</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.medication_id || item.medication_title}>
              <td>
                <strong>{item.medication_title}</strong>
                <div className="operations-muted">{item.item_code || "-"}</div>
              </td>
              <td>{formatQuantity(item.total_required_quantity)}</td>
              <td>{formatQuantity(item.available_quantity)}</td>
              <td>{formatQuantity(item.short_quantity)}</td>
              <td>
                {item.orders_count}
                <div className="operations-muted">
                  {(item.order_ids || []).map((id) => `#${id}`).join(", ")}
                </div>
              </td>
              <td>
                <ul className="operations-list">
                  {(item.items || []).map((row) => (
                    <li key={row.order_item_id}>
                      #{row.order_id} {row.customer_name}:{" "}
                      {formatQuantity(row.quantity)} {chip(row.stock_state, stockLabels)}
                    </li>
                  ))}
                </ul>
              </td>
              <td>{chip(item.procurement_state, procurementLabels)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const BatchOrdersTable = ({ orders }) => {
  if (!orders.length) {
    return <EmptyState>No selected orders found.</EmptyState>;
  }

  return (
    <div className="operations-table-wrap">
      <Table striped hover className="operations-table operations-batch-table">
        <thead>
          <tr>
            <th>Order</th>
            <th>Customer</th>
            <th>Status</th>
            <th>Items</th>
            <th>Pack State</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.order_id}>
              <td>#{order.order_id}</td>
              <td>
                <strong>{order.customer_name || "Customer"}</strong>
                <div className="operations-muted">{order.order_type}</div>
              </td>
              <td>{order.order_status_label}</td>
              <td>
                <ul className="operations-list">
                  {(order.items || []).map((item) => (
                    <li key={item.order_item_id}>
                      {item.medication_title} x {formatQuantity(item.quantity)}{" "}
                      {chip(item.stock_state, stockLabels)}
                    </li>
                  ))}
                </ul>
              </td>
              <td>
                {chip(order.pack_state, {
                  packed: packStateLabels.packed,
                  ready_to_pack: packStateLabels.ready_to_pack,
                  blocked_by_stock: packStateLabels.blocked_by_stock,
                })}
                <div className="operations-muted">
                  {order.packed_items_count}/{order.items_count} packed
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const ItemsTab = ({ items }) => {
  if (!items.length) {
    return <EmptyState />;
  }

  return (
    <div className="operations-table-wrap">
      <Table striped hover className="operations-table">
        <thead>
          <tr>
            <th>Medicine</th>
            <th>Total Needed</th>
            <th>Available</th>
            <th>Short</th>
            <th>Orders</th>
            <th>Procurement</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.medication_id || item.medication_title}>
              <td>
                <strong>{item.medication_title}</strong>
                <div className="operations-muted">{item.item_code || "-"}</div>
              </td>
              <td>{formatQuantity(item.total_required_quantity)}</td>
              <td>{formatQuantity(item.available_quantity)}</td>
              <td>{formatQuantity(item.short_quantity)}</td>
              <td>
                {item.orders_count}
                <div className="operations-muted">
                  {(item.order_ids || []).slice(0, 5).map((id) => `#${id}`).join(", ")}
                </div>
              </td>
              <td>{chip(item.procurement_state, procurementLabels)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const RoutesTab = ({ routes }) => {
  if (!routes.length) {
    return <EmptyState />;
  }

  return (
    <div className="operations-table-wrap">
      <Table striped hover className="operations-table">
        <thead>
          <tr>
            <th>Delivery Batch</th>
            <th>Orders</th>
            <th>Ready</th>
            <th>Blocked</th>
            <th>Pick First</th>
          </tr>
        </thead>
        <tbody>
          {routes.map((route) => (
            <tr
              key={`${route.district}-${route.city_id}-${route.delivery_type}`}
            >
              <td>
                <strong>{route.district}</strong>
                <div className="operations-muted">
                  {route.city_name} / {route.delivery_type_label}
                </div>
              </td>
              <td>{route.orders_count}</td>
              <td>{route.packable_orders_count}</td>
              <td>{route.blocked_orders_count}</td>
              <td>
                <ul className="operations-list">
                  {(route.picking_items || []).slice(0, 5).map((item) => (
                    <li key={item.medication_id || item.medication_title}>
                      {item.medication_title} x{" "}
                      {formatQuantity(item.total_required_quantity)}
                    </li>
                  ))}
                </ul>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

const ProcurementTab = ({
  items,
  actionLoading,
  markOrdered,
  markAllShortagesOrdered,
  hasActionItems,
}) => {
  if (!items.length) {
    return <EmptyState>There are no shortage items for these filters.</EmptyState>;
  }

  return (
    <>
      <div className="operations-toolbar">
        <div className="operations-muted">
          Marking ordered prevents the same shortage from being ordered again.
        </div>
        <Button
          bsStyle="warning"
          disabled={actionLoading || !hasActionItems}
          onClick={markAllShortagesOrdered}
        >
          Mark All Shortages Ordered
        </Button>
      </div>
      <div className="operations-table-wrap">
        <Table striped hover className="operations-table">
          <thead>
            <tr>
              <th>Medicine</th>
              <th>Needed</th>
              <th>Available</th>
              <th>Short</th>
              <th>Affected Orders</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.medication_id || item.medication_title}>
                <td>
                  <strong>{item.medication_title}</strong>
                  <div className="operations-muted">{item.item_code || "-"}</div>
                </td>
                <td>{formatQuantity(item.total_required_quantity)}</td>
                <td>{formatQuantity(item.available_quantity)}</td>
                <td>{formatQuantity(item.short_quantity)}</td>
                <td>
                  {item.orders_count}
                  <div className="operations-muted">
                    {(item.order_ids || []).slice(0, 6).map((id) => `#${id}`).join(", ")}
                  </div>
                </td>
                <td>{chip(item.procurement_state, procurementLabels)}</td>
                <td>
                  <Button
                    bsSize="xsmall"
                    bsStyle="info"
                    disabled={
                      actionLoading || item.procurement_state !== "not_ordered"
                    }
                    onClick={() => markOrdered(item.order_item_ids)}
                  >
                    Mark Ordered
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </>
  );
};

export default LoadingDashboard;
